import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import SignaturePad from 'signature_pad';
import { AwsSdkService } from 'src/app/shared/services/aws-sdk.service';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { CookieService } from 'ngx-cookie-service';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { LocalStoreService } from 'src/app/shared/services/local-store.service';
import { TermsAndConditionService } from 'src/app/views/administration/terms-conditions/terms-conditions.service';
import { SettingsService } from 'src/app/views/settings/settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Page } from 'src/app/shared/models/page';
import { AutoCompleteDataType } from 'src/app/shared/models/settings.model';

interface TermsDocument {
  id: string;
  tabLabel: string;
  title: string;
  subtitle: string;
  url: string;
  acknowledged: boolean;
}

@Component({
  selector: 'app-user-terms-conditions',
  templateUrl: './user-terms-conditions.component.html',
  styleUrls: ['./user-terms-conditions.component.css']
})
export class UserTermsConditionsComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

  @Input() isVisible: boolean = false;
  @Input() userId: number | null = null;
  @Input() fullName: string | null = null;
  @Input() userRole: string | null = 'Owner';
  /** The session the sign-in page just received (its cookies are written only after acceptance). The API takes the
   * accepting user from this session, so both calls below send it as the id / hash headers (2026-10-23). */
  @Input() sessionTokenId: string | number | null = null;
  @Input() sessionTokenHash: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onTermsAccepted = new EventEmitter<any>();

  // Canvas reference for the draw-signature tab. Material lazy-renders tab content,
  // so this is undefined until the Draw tab is first activated.
  @ViewChild('signatureCanvas') signatureCanvasRef?: ElementRef<HTMLCanvasElement>;

  showTermsSuccessPopup: boolean = false;
  isSubmitting: boolean = false;
  loadingMsg=''

  uploadedFiles: { file: File; name: string; size: number; displaySize: string; dataUrl?: string }[] = [];

  // ===== Signature capture state =====
  // 0 = Draw tab (default), 1 = Upload tab
  signatureTabIndex: number = 0;
  signaturePad: SignaturePad | null = null;
  hasDrawn: boolean = false;
  drawError: string | null = null;
  // Tracks which surface produced the currently-committed signature, for the review step.
  signatureSource: 'draw' | 'upload' | null = null;
  private resizeDebounce?: any;

  // Reactive form for the Identity & Signature step
  signatureForm!: FormGroup;

  // Country (paginated autocomplete)
  countryList: any[] = [];
  countrySearchText: string = '';
  selectedCountry: AutoCompleteDataType | null = null;
  selectedCountryCode: string = '';
  boundGetCountryAutoComplete!: (pageMeta: Page) => Observable<any>;

  dragActive = false;

  pdfZoom: number = 1;
  Math = Math;

  // Wizard state
  currentStep: number = 0;
  documents: any[] = [];

  userPolicyId:number | null = null

  private docPath = {
    supplierTermsUrl: 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_Supplier_TC_v1.0_FINAL.pdf',
    buyerTermsUrl : 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_Buyer_TC_v1.0_FINAL.pdf',
    dtrUrl:'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_Annex_DTR_v1.0_FINAL.pdf',
    scaUrl:'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_Annex_SCA_v1.0_FINAL.pdf',
    dpaUrl:'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_DPA_v1.0_FINAL.pdf',
    privacyPolicyUrl:'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/terms-conditions/VSMPS_Privacy_Policy_v1.0_FINAL.pdf'
  }

  termsResponse: any;

  userPolicyData:any=[]

  constructor(
    private awsSdkService: AwsSdkService,
    private webApiService: WebApiService,
    private cookieService: CookieService,
    private fullLoaderService: FullLoaderService,
    private termsAndConditionService: TermsAndConditionService,
    private snack: MatSnackBar,
    private sanitizer: DomSanitizer,
    private settingsService: SettingsService,
    private ls: LocalStoreService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.initSignatureForm();
  }

  ngOnInit(): void {
    this.buildDocuments();
    this.initCountryAutocomplete();
  }

  ngAfterViewChecked(): void {
    // Material renders the canvas only after the Draw tab becomes visible.
    // When it appears and the pad isn't built yet, initialize once.
    if (this.isSignatureStep && this.signatureTabIndex === 0 && !this.signaturePad && this.signatureCanvasRef) {
      this.initSignaturePad();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.resizeDebounce);
    this.signaturePad?.off();
    this.signaturePad = null;
  }

  private initSignatureForm(): void {
    this.signatureForm = this.fb.group({
      userFullName: [null, [Validators.required]],
      userDesignation: [null, [Validators.required]],
      userCompanyName: [null, [Validators.required]],
      country: [null, [Validators.required]],
      currentDate: [{ value: new Date().toISOString().split('T')[0], disabled: false }],
      controllerName:[null, [Validators.required]],
      controllerAddress:[null, [Validators.required]],
    });

    this.signatureForm.valueChanges.subscribe(() => {
      this.signatureForm.updateValueAndValidity({ emitEvent: false });
    });
  }

  private initCountryAutocomplete(): void {
    try {
      const cached = this.ls.getItem('countries');
      this.countryList = cached ? JSON.parse(cached) : [];
    } catch {
      this.countryList = [];
    }

    if (!Array.isArray(this.countryList) || this.countryList.length === 0) {
      this.settingsService.getAllCountriesByFliterKeyValue().subscribe({
        next: (res: any) => {
          this.countryList = Array.isArray(res?.rows) ? res.rows : [];
          try {
            this.ls.setItem('countries', JSON.stringify(this.countryList));
          } catch { }
          this.cdr.markForCheck();
        },
        error: () => {
          this.countryList = [];
        }
      });
    }
    console.log(this.countryList,'this.countryList')

    this.boundGetCountryAutoComplete = this.getCountryFilterKeyDataObservable.bind(this);
  }

  getAddressCountryControl(): FormControl {
    return this.signatureForm.get('country') as FormControl;
  }

  get currentDate(): string {
    return this.signatureForm?.get('currentDate')?.value || new Date().toISOString().split('T')[0];
  }

  filterFieldCountryNameDisplayFn = (_country: AutoCompleteDataType) => {
    return _country ? `${_country.name}` : undefined;
  };

  getCountryFilterKeyDataObservable(pageMeta: Page): Observable<any> {
    pageMeta.serachParam =
      pageMeta.serachParam || pageMeta.serachParam == ''
        ? pageMeta.serachParam
        : this.countrySearchText;
    this.countrySearchText = pageMeta.serachParam;
    return this.countryDataWithSearch(pageMeta.serachParam);
  }

  countryDataWithSearch(searchText: any): Observable<any> {
    const regex = new RegExp(searchText || '', 'i');
    return new Observable<any>(observer => {
      const data: any[] = Array.isArray(this.countryList) ? this.countryList : [];
      const filteredData = data.filter((item: any) => regex.test(item.name));
      observer.next({ rows: filteredData });
      observer.complete();
    });
  }

  onCountrySelected(value: AutoCompleteDataType): void {
    this.selectedCountry = value || null;
    this.selectedCountryCode = value?.code ? value.code : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole']) {
      this.buildDocuments();
    }
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      this.resetWizard();
    }
  }

  private buildDocuments(): void {
    // const isSupplier = this.userRole === 'Suppliers';
    // const portalUrl = isSupplier ? this.docPath.supplierTermsUrl : this.docPath.buyerTermsUrl;

    // if(isSupplier){
    //   this.documents = [
    //     {
    //       id: 'portal-tc',
    //       tabLabel:'Terms & Conditions',
    //       title: 'VSMPS Portal Terms & Conditions',
    //       subtitle: 'Marine Procurement System usage agreement',
    //       url: portalUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-pp',
    //       tabLabel:'Privacy Policy',
    //       title: 'VSMPS Portal Privacy Policy',
    //       subtitle: 'Marine Procurement System Privacy Policy',
    //       url: this.docPath.privacyPolicyUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-sca',
    //       tabLabel:'Supplier Compliance Addendum',
    //       title: 'VSMPS Portal Supplier Compliance Addendum',
    //       subtitle: 'Marine Procurement System Supplier Compliance Addendum',
    //       url: this.docPath.scaUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-dpa',
    //       tabLabel:'Data Processing Agreement',
    //       title: 'VSMPS Portal Data Processing Agreement ',
    //       subtitle: 'Marine Procurement System Data Processing Agreement ',
    //       url: this.docPath.dpaUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-dtr',
    //       tabLabel:'Default Transaction Rules Annex',
    //       title: 'VSMPS Default Transaction Rules Annex',
    //       subtitle: 'Marine Procurement System Default Transaction Rules Annex',
    //       url: this.docPath.dtrUrl,
    //       acknowledged: false
    //     }
    //   ];
    // }
    // else{
    //   this.documents = [
    //     {
    //       id: 'portal-tc',
    //       tabLabel:'Terms & Conditions',
    //       title: 'VSMPS Portal Terms & Conditions',
    //       subtitle: 'Marine Procurement System usage agreement',
    //       url: portalUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-pp',
    //       tabLabel:'Privacy Policy',
    //       title: 'VSMPS Portal Privacy Policy',
    //       subtitle: 'Marine Procurement System Privacy Policy',
    //       url: this.docPath.privacyPolicyUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-dpa',
    //       tabLabel:'Data Processing Agreement',
    //       title: 'VSMPS Portal Data Processing Agreement ',
    //       subtitle: 'Marine Procurement System Data Processing Agreement ',
    //       url: this.docPath.dpaUrl,
    //       acknowledged: false
    //     },
    //     {
    //       id: 'portal-dtr',
    //       tabLabel:'Default Transaction Rules Annex',
    //       title: 'VSMPS Default Transaction Rules Annex',
    //       subtitle: 'Marine Procurement System Default Transaction Rules Annex',
    //       url: this.docPath.dtrUrl,
    //       acknowledged: false
    //     }
    //   ];
    // }

    if(this.userRole === 'Suppliers'){
      this.getLatestUserPolicyByUserCategory('supplier')
    }
    else if(this.userRole === 'Owner'){
      this.getLatestUserPolicyByUserCategory('buyer')
    }
    

  }

  private resetWizard(): void {
    this.currentStep = 0;
    this.documents.forEach(d => (d.acknowledged = false));
    this.uploadedFiles = [];
    this.selectedCountry = null;
    this.selectedCountryCode = '';
    this.signatureForm.reset({
      userFullName: null,
      userDesignation: null,
      userCompanyName: null,
      country: null,
      currentDate: new Date().toISOString().split('T')[0],
      controllerName:null,
      controllerAddress:null,
    });

    // Reset signature capture state.
    this.signatureTabIndex = 0;
    this.hasDrawn = false;
    this.drawError = null;
    this.signatureSource = null;
    this.signaturePad?.clear();
  }

  // ===== Step model =====
  // 0: Welcome
  // 1..N: Document review (one per document)
  // N+1: Identity & Signature
  // N+2: Final review
  get totalSteps(): number {
    return this.documents.length + 3;
  }

  get stepIndices(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i);
  }

  get isWelcomeStep(): boolean {
    return this.currentStep === 0;
  }

  get isDocumentStep(): boolean {
    return this.currentStep >= 1 && this.currentStep <= this.documents.length;
  }

  get isSignatureStep(): boolean {
    return this.currentStep === this.documents.length + 1;
  }

  get isReviewStep(): boolean {
    return this.currentStep === this.documents.length + 2;
  }

  get currentDocument(): TermsDocument | null {
    if (!this.isDocumentStep) return null;
    return this.documents[this.currentStep - 1] || null;
  }

  get currentStepLabel(): string {
    if (this.isWelcomeStep) return 'Welcome';
    if (this.isDocumentStep) return this.currentDocument?.title || 'Document';
    if (this.isSignatureStep) return 'Identity & Signature';
    if (this.isReviewStep) return 'Review & Submit';
    return '';
  }

  get progressPercent(): number {
    return Math.round((this.currentStep / (this.totalSteps - 1)) * 100);
  }

  get allDocumentsAcknowledged(): boolean {
    return this.documents.every(d => d.acknowledged);
  }

  canGoNext(): boolean {
    if (this.isWelcomeStep) return true;
    if (this.isDocumentStep) return !!this.currentDocument?.acknowledged;
    if (this.isSignatureStep) {
      const formValid =
      
      this.signatureForm.get('userFullName')?.valid &&
      this.signatureForm.get('userDesignation')?.valid &&
      this.signatureForm.get('userCompanyName')?.valid &&
      this.signatureForm.get('country')?.valid &&
      this.signatureForm.get('controllerName')?.valid &&
      this.signatureForm.get('controllerAddress')?.valid &&
      this.signatureForm.get('currentDate')?.valid;


      const hasSignature = this.uploadedFiles.length === 1;

      return !!formValid && hasSignature;
      // return this.signatureForm.valid && this.uploadedFiles.length === 1;
    }
    return true;
  }

  nextStep(): void {
    this.signatureForm.updateValueAndValidity();

    if (!this.canGoNext()) {
      this.showStepValidationError();
      return;
    }
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    // Allow going back to any prior step, but block jumping forward past unmet gates.
    if (step < this.currentStep) {
      this.currentStep = step;
      return;
    }
    if (step === this.currentStep) return;
    // Jumping forward — verify all intermediate steps pass validation.
    for (let s = this.currentStep; s < step; s++) {
      const savedStep = this.currentStep;
      this.currentStep = s;
      const ok = this.canGoNext();
      this.currentStep = savedStep;
      if (!ok) {
        this.showStepValidationError();
        return;
      }
    }
    this.currentStep = step;
  }

  private showStepValidationError(): void {
    if (this.isDocumentStep) {
      this.snack.open('Please confirm you have read this document before continuing.', 'OK', { duration: 3000, panelClass: 'custom-snack' });
    } else if (this.isSignatureStep) {
      this.signatureForm.markAllAsTouched();
      const missing: string[] = [];
      const f = this.signatureForm.controls;
      
      if (f['userFullName'].invalid) missing.push('Full Name');
      if (f['userDesignation'].invalid) missing.push('Designation');
      if (f['userCompanyName'].invalid) missing.push('Company');
      if (f['country'].invalid) missing.push('Country');
      if (f['controllerName'].invalid) missing.push('Controller Name');
      if (f['controllerAddress'].invalid) missing.push('Controller Address');
      if (this.uploadedFiles.length === 0) missing.push('Signature');
      if (this.uploadedFiles.length > 1) missing.push('Only one signature file');
      this.snack.open(`Missing: ${missing.join(', ')}`, 'OK', { duration: 3000, panelClass: 'custom-snack' });
    }
  }

  // ===== Existing helpers =====
  closeModals(): void {
    this.isVisible = false;
    this.visibleChange.emit(this.isVisible);
  }

  zoomIn(): void {
    if (this.pdfZoom < 2) {
      this.pdfZoom = Math.round((this.pdfZoom + 0.1) * 10) / 10;
    }
  }

  zoomOut(): void {
    if (this.pdfZoom > 0.5) {
      this.pdfZoom = Math.round((this.pdfZoom - 0.1) * 10) / 10;
    }
  }

  resetZoom(): void {
    this.pdfZoom = 1;
  }

  triggerFileBrowse(input: HTMLInputElement): void {
    input.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(): void {
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFileChange(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.handleFiles(files);
    (event.target as HTMLInputElement).value = '';
    this.cdr.detectChanges();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    if (this.uploadedFiles.length === 0) {
      this.signatureSource = null;
    }
  }

  private handleFiles(files: FileList | null): void {
    if (!files) return;

    Array.from(files).forEach((file) => {
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.snack.open(`Only JPG, JPEG, and PNG files are allowed.`, 'OK', { duration: 3000, panelClass: 'custom-snack' });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        // Uploaded file replaces any previously-committed (drawn or uploaded) signature
        // so the wizard never carries more than one.
        this.uploadedFiles = [{
          file,
          name: file.name,
          size: file.size,
          displaySize: this.formatFileSize(file.size),
          dataUrl: e.target.result
        }];
        this.signatureSource = 'upload';
      };
      reader.readAsDataURL(file);
    });
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async acceptTerms(): Promise<void> {
    this.signatureForm.markAllAsTouched();
    const missing: string[] = [];
    const f = this.signatureForm.controls;
    
    if (f['userFullName'].invalid) missing.push('Full Name');
    if (f['userDesignation'].invalid) missing.push('Designation');
    if (f['userCompanyName'].invalid) missing.push('Company');
    if (f['country'].invalid) missing.push('Country');
    if (f['controllerName'].invalid) missing.push('Controller Name');
    if (f['controllerAddress'].invalid) missing.push('Controller Address');
    if (!this.userId) missing.push('User Info');
    if (!this.userPolicyId) missing.push('Policy Info');
    if (!this.uploadedFiles.length) missing.push('Signature');
    if (!this.allDocumentsAcknowledged) missing.push('Document acknowledgement');

    if (missing.length) {
      this.snack.open(`Missing: ${missing.join(', ')}`, 'OK', { duration: 3000, panelClass: 'custom-snack' });
      return;
    }

    if (this.uploadedFiles.length > 1) {
      this.snack.open(`More than one signature file`, 'OK', { duration: 3000, panelClass: 'custom-snack' });
      return;
    }

    this.isSubmitting = true;
    this.loadingMsg = 'Uploading signature and submitting...'

    try {
      const documentDetails = await this.uploadAttachmentsToS3();

      const formValue = this.signatureForm.getRawValue();
      const backendPayload = {
        userId: this.userId,
        userPolicyId:this.userPolicyId || null,
        userFullName: formValue.userFullName || '',
        userDesignation: formValue.userDesignation || '',
        userCompanyName: formValue.userCompanyName || '',
        countryName: this.selectedCountry?.name || null,
        countryCode: this.selectedCountryCode || null,
        controllerName: formValue.controllerName || '',
        controllerAddress: formValue.controllerAddress || '',
        termsAccepted: 1,
        termsVersion: 1,
        singatureS3path: documentDetails?.[0]?.s3path || null
        // singatureS3path: 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/user/signature/1777559226596-p77fho75wn-dining_set.jpg'
      };

      const response = await this.termsAndConditionService.acceptTermsAndConditions(backendPayload, this.sessionHttpOptions());

      this.termsResponse = response.result;

      if (response.result.termsAccepted) {
        this.isSubmitting = true
        this.loadingMsg = 'Sending Terms acceptance confirmation mail...'
        const params = {
          userFullName:formValue.userFullName,
          userCompanyName: formValue.userCompanyName ,
          email:response.result.email,
          termsData:response.files ?? [],
          userId:this.userId
        }
        await this.termsAndConditionService.sendTermsAcceptanceMail(params, this.sessionHttpOptions())
        this.isSubmitting = false
        this.loadingMsg  = ''
        this.isVisible = false;
        this.visibleChange.emit(false);
        this.showTermsSuccessPopup = true;
      }
    } catch (err) {
      console.error('Failed to submit terms acceptance', err);
    } finally {
      this.isSubmitting = false;
      this.loadingMsg = ''
    }
  }

  private sessionHttpOptions(): { headers?: HttpHeaders } {
    if (this.sessionTokenId === null || this.sessionTokenId === undefined || !this.sessionTokenHash) return {};
    return { headers: new HttpHeaders({ id: String(this.sessionTokenId), hash: String(this.sessionTokenHash) }) };
  }

  private async uploadAttachmentsToS3(): Promise<{ documentName: string; documentType: string; s3path: string }[]> {
    const uploads: { documentName: string; documentType: string; s3path: string }[] = [];

    for (const item of this.uploadedFiles) {
      const detail = {
        documentName: item.name,
        documentType: item.file.type || '',
        s3path: ''
      };

      const key = `user/signature/${Date.now()}-${Math.random().toString(36).slice(2)}-${item.name}`;
      const s3Params = { fileKey: key, fileContentType: item.file.type || 'application/octet-stream' };

      try {
        // 2026-10-26: the presign call needs the session too, and this step runs before the session cookies exist.
        const signedUrl = await this.awsSdkService.getSignedUrlPromise(s3Params, this.sessionHttpOptions());
        await this.webApiService.putRequestExternalApi(signedUrl, item.file);
        detail.s3path = this.awsSdkService.extractS3LocationFromUrl(signedUrl);
      } catch (err) {
        console.error('Failed to upload attachment to S3', item.name, err);
      }

      uploads.push(detail);
    }

    return uploads;
  }

  proceed() {
    this.showTermsSuccessPopup = false;
    this.onTermsAccepted.emit(this.termsResponse);
  }


  // ============================================================
  //  SIGNATURE CAPTURE — Draw tab
  // ============================================================

  // Initialises the signature_pad instance bound to the canvas.
  // Called lazily from ngAfterViewChecked because Material renders tab content on demand.
  private initSignaturePad(): void {
    if (!this.signatureCanvasRef) return;
    const canvas = this.signatureCanvasRef.nativeElement;

    this.resizeCanvasOnDpr();

    this.signaturePad = new SignaturePad(canvas, {
      minWidth: 0.6,
      maxWidth: 2.2,
      penColor: '#111827',
      // Transparent pad — the white card background shows through and the PNG
      // exported via toDataURL stays transparent so the signed PDF can overlay it cleanly.
      backgroundColor: 'rgba(0,0,0,0)',
    });

    // Each completed stroke flips hasDrawn so the "Use this signature" button enables.
    this.signaturePad.addEventListener('endStroke', () => {
      this.hasDrawn = !this.signaturePad!.isEmpty();
      this.drawError = null;
      this.cdr.markForCheck();
    });
  }

  // Sizes the canvas backing store for the device pixel ratio so strokes stay crisp
  // on retina/high-DPI screens. Preserves any in-progress drawing across resizes.
  private resizeCanvasOnDpr(): void {
    if (!this.signatureCanvasRef) return;
    const canvas = this.signatureCanvasRef.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    const data = this.signaturePad && !this.signaturePad.isEmpty()
      ? this.signaturePad.toData()
      : null;

    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    if (this.signaturePad) {
      this.signaturePad.clear();
      if (data) this.signaturePad.fromData(data);
    }
  }

  clearPad(): void {
    this.signaturePad?.clear();
    this.hasDrawn = false;
    this.drawError = null;
  }

  async commitDrawnSignature(): Promise<void> {
    if (!this.signaturePad || this.signaturePad.isEmpty()) return;
    try {
      const dataUrl = this.signaturePad.toDataURL('image/png');
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `signature_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Replace any prior signature — wizard only carries one.
      this.uploadedFiles = [{
        file,
        name: fileName,
        size: file.size,
        displaySize: this.formatFileSize(file.size),
        dataUrl,
      }];
      this.signatureSource = 'draw';
      this.drawError = null;
      this.clearPad();
    } catch (err) {
      console.error('commit drawn signature failed', err);
      this.drawError = 'Could not save the drawn signature. Please try again.';
    }
  }

  onSigTabChange(idx: number): void {
    this.signatureTabIndex = idx;
    if (idx === 0) {
      // Re-size on tab activate — viewport may have changed while the canvas was detached.
      setTimeout(() => {
        if (!this.signaturePad) this.initSignaturePad();
        else this.resizeCanvasOnDpr();
      }, 0);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    clearTimeout(this.resizeDebounce);
    this.resizeDebounce = setTimeout(() => this.resizeCanvasOnDpr(), 150);
  }

  async getLatestUserPolicyByUserCategory(userType: string) {
    this.isSubmitting = true
    this.loadingMsg = 'Fetching user terms policy data'
    this.userPolicyData = await this.termsAndConditionService.getLatestUserPolicyByUserCategory(userType)

    this.documents = [];

    if (
      Array.isArray(this.userPolicyData) &&
      this.userPolicyData.length > 0 &&
      Array.isArray(this.userPolicyData[0]?.policyDocumentData) &&
      this.userPolicyData[0].policyDocumentData.length > 0
    ) {

      this.userPolicyId = this.userPolicyData[0].policyId
      this.userPolicyData[0].policyDocumentData.forEach((doc: any) => {
        this.documents.push({
          id: doc.mappedDocId,
          tabLabel: doc.termsDocumentTypeName,
          // title: `${doc.documentTitle} ${doc.documentVersion}`,
          title: `${doc.documentTitle}`,
          subtitle: doc.documentTitle,
          url: doc.documentPath,
          formUrl: doc.documentFormPath,
          isSignatureNeed: doc.isSignatureNeeded,
          version:doc.documentVersion,
          acknowledged: false,

        });
      });
    }
    this.isSubmitting = false
    this.loadingMsg = ''
  }
}
